import React from 'react'
import { InfoIcon } from '@toasttab/buffet-pui-icons'
import { useFormatCurrency } from '../../hooks/useFormatCurrency'
import { useGetOrderPriceSummary } from '../../api/bookings/useGetOrderPriceSummary'
import classNames from 'classnames'
import { isPastUtcDate } from '../../utils/isPastUtcDate'

type OrderSummaryProps = {
  bookingGuid: string
  orderSummaryType: OrderSummaryType
  bookableName?: string
  paymentRefundableCancellationDatetime?: string | null
  hasPaymentBeenRefunded: boolean
}

export enum OrderSummaryType {
  AMOUNT_DUE = 'AMOUNT_DUE',
  AMOUNT_PAID = 'AMOUNT_PAID'
}

export const OrderSummary = ({
  bookingGuid,
  orderSummaryType,
  bookableName,
  paymentRefundableCancellationDatetime,
  hasPaymentBeenRefunded
}: OrderSummaryProps) => {
  const { data: orderPriceSummary } = useGetOrderPriceSummary(bookingGuid)

  const formatCurrency = useFormatCurrency()

  const selectionQuantity = orderPriceSummary?.selections[0].quantity

  const selectionUnitPrice = formatCurrency(
    orderPriceSummary?.selections[0].unitPrice ?? 0
  )

  const subtotal = formatCurrency(orderPriceSummary?.subTotal ?? 0)
  const tax = formatCurrency(orderPriceSummary?.tax ?? 0, {
    trailingZeros: true
  })
  const total = formatCurrency(orderPriceSummary?.total ?? 0, {
    trailingZeros: true
  })

  const hasBackground = orderSummaryType === OrderSummaryType.AMOUNT_PAID
  const needsPadding = orderSummaryType === OrderSummaryType.AMOUNT_PAID

  return (
    <div className='flex flex-col'>
      <div
        className={classNames('flex flex-col gap-4 rounded-md', {
          'p-4': needsPadding,
          'bg-gray-0': hasBackground
        })}
      >
        <div>
          <h4 className='type-large font-semibold'>Order summary</h4>
        </div>
        <div>
          <div className='flex justify-between'>
            <p>
              Reservation
              {` x ${selectionQuantity}`}
            </p>
            <p>{selectionUnitPrice}</p>
          </div>
          {bookableName && (
            <div>
              <p className='text-secondary type-subhead'>
                {bookableName} Payment
              </p>
            </div>
          )}
          <div className='border-b pt-2 mb-2' />
          <div className='flex justify-between my-2'>
            <p>Subtotal</p>
            <p>{subtotal}</p>
          </div>
          <div className='flex justify-between'>
            <div className='flex items-center gap-1'>
              <p>Taxes & Fees</p>
              <InfoIcon />
            </div>
            <p>{tax}</p>
          </div>
          <div className='border-b pt-2 mb-2' />
          <div className='flex justify-between'>
            <p>Total</p>
            <p>{total}</p>
          </div>
          <div className='border-b pt-2 mb-2' />
          <div className='flex justify-between font-semibold items-center'>
            {orderSummaryType === OrderSummaryType.AMOUNT_DUE ? (
              <div className='flex justify-between type-headline-5 font-bold w-full'>
                <span>Amount Due</span>
                <span>{total}</span>
              </div>
            ) : (
              <div className='flex justify-between font-semibold w-full'>
                <span>Amount Paid</span>
                <span>{total}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      {paymentRefundableCancellationDatetime &&
        isPastUtcDate(new Date(paymentRefundableCancellationDatetime)) &&
        !hasPaymentBeenRefunded && (
          <div className='flex gap-2 text-secondary type-subhead'>
            <InfoIcon /> Booking is no longer refundable.
          </div>
        )}
    </div>
  )
}
